import React, { useEffect, useState, VFC } from 'react';
import { hesselApiTypes } from '../../../lib/api/models/hessel-api';
import { hesselViewModels } from '../../../lib/view-models';
import { formatPrice } from '../../../utils/helpers';
import { Spinner } from '../../shared/spinner';
import {
    SummarySection,
    ErrorText,
    SpinnerWrapper,
    StyledLeasingInformationTableRow,
    StyledTextWithDisclaimer,
} from './lease-manager-financing-summary.styled';
import { ProductDetailsStore } from '../../../lib/state/hessel-site/product-details';
import { usePriceByOwnership } from '../hooks/use-price-by-ownership';
import { ProductDetailsPage as UmbracoProductDetailsPage } from '../../../lib/api/models/umbraco';
import { Abbreviations } from '../../../constants/units-and-abbreviations';
import { getPriceForAllEquipment } from '../../../lib/state/hessel-site/product-details/price.helper';

type LeaseManagerFinancingSummaryProps = {
    product: hesselViewModels.ProductDetails;
    page: UmbracoProductDetailsPage;
    selectedTab: 'Finansiel Leasing' | 'Operationel Leasing';
};

export const LeaseManagerFinancingSummary: VFC<LeaseManagerFinancingSummaryProps> = ({ product, page, selectedTab }) => {
    const { financialPurchaseDownPaymentPercentage, computedCashPrice } = ProductDetailsStore.useStoreState((state) => state);

    const duration = product.purchaseTypes.financing.durationMonths;
    const downPaymentPercentage = financialPurchaseDownPaymentPercentage;
    const [leaseManagerFinancingToShow, setLeaseManagerFinancingToShow] = useState<hesselApiTypes.LeaseManagerFinancing | undefined>(undefined);

    const {
        leaseManagerFinancing,
        leaseManagerError: error,
        leaseManagerIsLoading: isLoading,
        leaseManagerIsValidating: isValidating,
    } = usePriceByOwnership(page);

    const [showSpinner, setShowSpinner] = useState(false);

    useEffect(() => {
        if (!leaseManagerFinancing) {
            return;
        }
        if (leaseManagerFinancing && !error) {
            setLeaseManagerFinancingToShow(leaseManagerFinancing);
            return;
        }
        setLeaseManagerFinancingToShow(undefined);
    }, [leaseManagerFinancing, error]);

    useEffect(() => {
        setShowSpinner(false);
        if (isLoading || isValidating) {
            const timeout = setTimeout(() => setShowSpinner(true), 1000);
            return () => clearTimeout(timeout);
        }
    }, [computedCashPrice, duration, downPaymentPercentage, leaseManagerFinancingToShow, isLoading, isValidating]);

    return (
        <div>
            {error === 'Data Fetch Failed' ? (
                <ErrorText>Vi kan desværre ikke beregne finansieringspriser i øjeblikket. Prøv venligst igen senere.</ErrorText>
            ) : null}

            {!!leaseManagerFinancingToShow && !showSpinner && !error ? (
                <>
                    <SummarySection>
                        <StyledLeasingInformationTableRow>
                            <strong>Leasingtype</strong>
                            <span>{selectedTab === 'Finansiel Leasing' ? 'Finansiel' : 'Operationel'}</span>
                        </StyledLeasingInformationTableRow>
                        <StyledLeasingInformationTableRow>
                            <strong>Månedlig ydelse</strong>
                            <StyledTextWithDisclaimer>{`${formatPrice(
                                leaseManagerFinancingToShow.baseMonthlyPrice + (product ? getPriceForAllEquipment(product, selectedTab) ?? 0 : 0)
                            )} ${Abbreviations.KR_SLASH_MDR}`}</StyledTextWithDisclaimer>
                        </StyledLeasingInformationTableRow>
                        <StyledLeasingInformationTableRow>
                            <strong>Løbetid</strong>
                            <span>{`${leaseManagerFinancingToShow.durationMonths} ${Abbreviations.MDR}`}</span>
                        </StyledLeasingInformationTableRow>
                        <StyledLeasingInformationTableRow>
                            <strong>Kilometer pr. år</strong>
                            <span>{`${formatPrice(leaseManagerFinancingToShow?.kilometersPerYear ?? 0)} km`}</span>
                        </StyledLeasingInformationTableRow>
                        <StyledLeasingInformationTableRow>
                            <strong>Udbetaling</strong>
                            <StyledTextWithDisclaimer>{`${formatPrice(leaseManagerFinancingToShow?.downPayment ?? 0)} ${
                                Abbreviations.KR
                            }`}</StyledTextWithDisclaimer>
                        </StyledLeasingInformationTableRow>
                        {leaseManagerFinancingToShow.residualValue && selectedTab === 'Finansiel Leasing' && (
                            <StyledLeasingInformationTableRow>
                                <strong>Restværdi</strong>
                                <StyledTextWithDisclaimer>{`${formatPrice(leaseManagerFinancingToShow.residualValue)} ${
                                    Abbreviations.KR
                                }`}</StyledTextWithDisclaimer>
                            </StyledLeasingInformationTableRow>
                        )}
                    </SummarySection>
                </>
            ) : null}

            {showSpinner && !error ? (
                <SpinnerWrapper>
                    <Spinner size="medium" variant="light" />
                </SpinnerWrapper>
            ) : null}
        </div>
    );
};
